/**
 * Admin → People (ADR 0069, 0072): the onboarding roster and the platform
 * roles DOIT AI assigns. Reading is open to admins and viewers; assigning
 * and revoking is admin-only, which the backend enforces.
 */
import { acquireToken, USE_REAL_AUTH } from "../auth/msal";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

async function headers(devUser?: string): Promise<Record<string, string>> {
  if (USE_REAL_AUTH) {
    const t = await acquireToken();
    return t ? { Authorization: `Bearer ${t.token}` } : {};
  }
  return devUser ? { "X-Dev-User": devUser } : {};
}

async function ok<T>(r: Response): Promise<T> {
  if (!r.ok) {
    let detail = "";
    try {
      const body = (await r.json()) as { detail?: unknown };
      detail = typeof body.detail === "string" ? body.detail : "";
    } catch {
      /* not JSON */
    }
    throw new Error(detail || `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

/** One person's state for one onboarding video. */
export interface VideoState {
  /** watched = the current version; outdated = an earlier one only. */
  status: "watched" | "outdated" | "not_watched";
  version: string;
  completed_at: string;
}

export interface Person {
  user_oid: string;
  email: string;
  display_name: string;
  teams: string[];
  first_seen_at: string;
  last_seen_at: string;
  roles: string[];
  videos: Record<string, VideoState>;
}

export interface PeopleVideo {
  id: string;
  version: string;
  title: string;
  applies_to: "*" | string[];
}

export interface PeopleResponse {
  mode: "off" | "pilot" | "on";
  videos: PeopleVideo[];
  assignable_roles: { role: string; label: string }[];
  people: Person[];
}

export async function getPeople(devUser?: string): Promise<PeopleResponse> {
  const r = await fetch(`${API_BASE}/api/admin/people`, {
    cache: "no-store",
    headers: await headers(devUser),
  });
  return ok<PeopleResponse>(r);
}

export async function setRole(
  userOid: string,
  role: string,
  granted: boolean,
  devUser?: string,
): Promise<{ changed: boolean }> {
  const r = await fetch(
    `${API_BASE}/api/admin/people/${encodeURIComponent(userOid)}/roles/${encodeURIComponent(role)}`,
    { method: granted ? "PUT" : "DELETE", headers: await headers(devUser) },
  );
  return ok<{ changed: boolean }>(r);
}
